import * as Progress from '@radix-ui/react-progress';
import cn from 'classnames';
import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { ReportGenerationErrorCode } from '@/shared/api/protocol-ts/model/dto_report_generation_error_codes_pb';
import { Report } from '@/shared/api/protocol-ts/model/dto_report_pb';
import clearCat from '@/shared/assets/images/cats/clear.png';
import errorCat from '@/shared/assets/images/cats/error.png';
import { useAppSelector, useMedia } from '@/shared/hooks';
import { StatusCatImage } from '@/shared/ui';

import { reportGeneratingErrorMessages } from '@/entities/reports';

import {
  i18n as highLoadI18n,
  highLoadModel,
  isHighLoadLevel,
} from '@/features/highLoad';
import { getReportGenerationInterval } from '@/features/reportRow';

import {
  MAX_PSEUDO_PROGRESS,
  PENDING_PROGRESS,
  UPLOAD_WAITING_PROGRESS,
} from '../../config';
import { getRelativeProgress } from '../../lib';

import styles from './ReportGenerationStatus.module.scss';

type ReportGenerationStatusProps = {
  report: Report | undefined;
  className?: string;
  compact?: boolean;
  testID?: string;
};

export const ReportGenerationStatus: FC<ReportGenerationStatusProps> = (
  props,
) => {
  const { report, className, compact, testID } = props;
  const { formatMessage } = useIntl();
  const { isMobile } = useMedia();

  const [progress, setProgress] = useState<number>(0);

  const reportType = report?.Type;
  const status = report?.Status;
  const createdAt = report?.Created?.At?.toDate();
  const isWaitingForUploads = status?.WaitingForUploadsToComplete;
  const isPending = status?.Pending;
  const isInProgress = Boolean(status?.InProgress);
  const isFailed = Boolean(status?.Failed);
  const errorCode = status?.Failed?.Code;

  const isGenerating = isInProgress || isPending || isWaitingForUploads;

  const highLoadLevel = useAppSelector(
    highLoadModel.selectors.selectReportHighLoadLevel(report),
  );
  const showHighLoadNotice = isHighLoadLevel(highLoadLevel);
  const highLoadMessages = isPending
    ? highLoadI18n.reportPipelineHighLoadMessages
    : highLoadI18n.uploadHighLoadMessages;

  const hasGenerationError =
    errorCode !== undefined &&
    Object.values(ReportGenerationErrorCode).includes(errorCode);

  const generationErrorMessage = hasGenerationError
    ? formatMessage(reportGeneratingErrorMessages[errorCode])
    : formatMessage({
        id: 'reportGenerationError.unexpectedError',
        defaultMessage:
          'Something went wrong. Try again later or contact our support team.',
      });

  const getStatusImageSrc = () => {
    if (isGenerating) {
      return clearCat;
    }

    if (isFailed) {
      return errorCat;
    }

    return undefined;
  };

  const getStatusMessage = () => {
    if (isGenerating) {
      return formatMessage({
        id: 'reportGenerationStatus.generatingReport',
        defaultMessage: 'Generating report',
      });
    }

    if (isFailed) {
      return generationErrorMessage;
    }

    return undefined;
  };

  const statusImageSrc = getStatusImageSrc();
  const statusMessage = getStatusMessage();

  useEffect(() => {
    if (reportType) {
      const pseudoProgress = setInterval(() => {
        const relativeProgress = getRelativeProgress(reportType, createdAt);

        setProgress((currentProgress) => {
          if (isWaitingForUploads) {
            return UPLOAD_WAITING_PROGRESS;
          }

          if (isPending) {
            return PENDING_PROGRESS;
          }

          if (relativeProgress > MAX_PSEUDO_PROGRESS) {
            return MAX_PSEUDO_PROGRESS;
          }

          if (relativeProgress > currentProgress) {
            return relativeProgress;
          }

          return currentProgress;
        });

        if (relativeProgress > MAX_PSEUDO_PROGRESS) {
          clearInterval(pseudoProgress);
        }
      }, getReportGenerationInterval(reportType));

      if (isFailed) {
        clearInterval(pseudoProgress);
        setProgress(0);
      }

      return () => clearInterval(pseudoProgress);
    }
  }, [isFailed, reportType, createdAt, isPending, isWaitingForUploads]);

  return (
    <div
      className={cn(styles.container, compact && styles.compact, className)}
      data-testid={testID}
      data-component="report-generation-status"
    >
      {statusImageSrc && !compact && (
        <StatusCatImage
          statusImageSrc={statusImageSrc}
          altText={statusMessage}
          isLoading={isGenerating}
        />
      )}

      <div className={cn(styles.status, { p2: !isMobile, p2m: isMobile })}>
        <div className={styles.statusMessage}>{statusMessage}</div>

        {isGenerating && (
          <Progress.Root
            className={styles.progressBar}
            value={progress}
            max={100}
          >
            <Progress.Indicator
              className={cn(styles.progressIndicator)}
              style={{ transform: `translateX(-${100 - progress}%)` }}
            />
          </Progress.Root>
        )}
      </div>

      {isGenerating && showHighLoadNotice && (
        <div className={cn(styles.highLoadNotice, 'p3')}>
          {formatMessage(highLoadMessages[highLoadLevel])}
        </div>
      )}
    </div>
  );
};
